import { Dispatch, SetStateAction, useState } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import mainBackgroundUrl from '@images/main-background.png';
import { useAdminPanelStore } from '@store/adminPanelStore';
import Logo from '@logoFeature';
import Chat from '@chatFeature';
import Avatar from '@avatarFeature';
import AdminPanel from '@adminPanelFeature';

import { authenticatedLayoutTexts } from './constants';

interface ShowAvatarButtonProps {
    setIsAvatarVisible: Dispatch<SetStateAction<boolean>>;
}

const ShowAvatarButton = ({ setIsAvatarVisible }: ShowAvatarButtonProps) => {
    const theme = useTheme();
    return (
        <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{
                position: 'absolute',
                top: theme.spacing(2),
                right: theme.spacing(2),
            }}
        >
            <Typography variant='body1' color={theme.palette.customColors.white} fontWeight={300}>
                {authenticatedLayoutTexts.openAvatar}
            </Typography>
            <IconButton
                onClick={() => setIsAvatarVisible(true)}
                title={authenticatedLayoutTexts.openAvatar}
            >
                <ArrowBackIosNewIcon sx={{ color: 'white', width: 20, height: 20 }} />
            </IconButton>
        </Stack>
    );
};

const AuthenticatedLayout = () => {
    const theme = useTheme();
    const [isAvatarVisible, setIsAvatarVisible] = useState(false);
    const isAdminPanelOpen = useAdminPanelStore((state) => state.isOpen);
    return (
        <>
            <Stack
                alignItems='center'
                justifyContent='center'
                sx={{
                    backgroundImage: `url(${mainBackgroundUrl})`,
                    height: '100vh',
                    maxHeight: '100vh',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    padding: theme.spacing(2),
                }}
            >
                <Stack
                    direction='row'
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        height: '100%',
                        borderRadius: `calc(${theme.borderRadius.medium} - ${theme.borderWidth.medium})`,
                        backdropFilter: 'blur(80px)',
                        boxShadow: theme.customShadows.primary,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: `-${theme.borderWidth.medium}`,
                            borderRadius: theme.borderRadius.medium,
                            background: theme.gradients.primaryRadialGradient,
                            zIndex: -1,
                            WebkitMask:
                                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            padding: `${theme.borderWidth.medium}`,
                        },
                    }}
                >
                    <Stack
                        sx={{
                            flex: 7,
                            minWidth: 0,
                        }}
                        p={2.625}
                        gap={1}
                    >
                        <Logo />
                        <Chat />
                    </Stack>
                    {isAvatarVisible ? (
                        <Avatar setIsAvatarVisible={setIsAvatarVisible} />
                    ) : (
                        <ShowAvatarButton setIsAvatarVisible={setIsAvatarVisible} />
                    )}
                </Stack>
            </Stack>
            {isAdminPanelOpen && <AdminPanel />}
        </>
    );
};

export default AuthenticatedLayout;
